import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import PublicLayout from "@/components/PublicLayout";
import { api } from "@/lib/api";
import { CheckCircle2, AlertTriangle, Award, Building2 } from "lucide-react";
import LinkedInShare from "@/components/LinkedInShare";
import { track } from "@/lib/analytics";

export default function Verificar() {
  const { folio } = useParams();
  const [state, setState] = useState({ loading: true });

  useEffect(() => {
    api.get(`/public/verify/${folio}`)
      .then((r) => { setState({ loading: false, data: r.data }); track("certificate_verified", { folio, valid: true }); })
      .catch(() => { setState({ loading: false, invalid: true }); track("certificate_verified", { folio, valid: false }); });
  }, [folio]);

  if (state.loading) return (
    <PublicLayout>
      <div className="max-w-2xl mx-auto px-4 py-24 text-center text-gray-600">Verificando...</div>
    </PublicLayout>
  );

  if (state.invalid) return (
    <PublicLayout>
      <section className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-24 text-center" data-testid="verify-invalid">
        <div className="mx-auto w-14 h-14 rounded-full bg-red-500/10 border border-red-500/30 flex items-center justify-center">
          <AlertTriangle className="w-7 h-7 text-red-400" />
        </div>
        <h1 className="mt-6 text-3xl font-bold text-[#0B0B0B] font-display">Folio no encontrado</h1>
        <p className="mt-3 text-[#0B0B0B]">
          El folio <span className="font-mono text-[#0B0B0B]">{folio}</span> no corresponde a ningún certificado emitido por DCC.
        </p>
        <p className="mt-6 text-sm text-gray-500">
          Si crees que hay un error, contacta a{" "}
          <a href="mailto:certificados@dcc.mx" className="text-[#F26A21] hover:text-[#0B0B0B]">certificados@dcc.mx</a>
        </p>
        <Link to="/" className="mt-8 inline-block text-[#F26A21] hover:text-[#0B0B0B]">← Volver al inicio</Link>
      </section>
    </PublicLayout>
  );

  const d = state.data;
  return (
    <PublicLayout>
      <section className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-16" data-testid="verify-valid">
        <div className="p-8 rounded-2xl border border-emerald-500/40 bg-gradient-to-br from-emerald-500/10 via-[#0F172A] to-[#0B0F14] relative overflow-hidden">
          <div className="absolute inset-0 bg-grid opacity-30" />
          <div className="relative">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-emerald-500/20 border border-emerald-500/40 flex items-center justify-center">
                <CheckCircle2 className="w-6 h-6 text-emerald-300" />
              </div>
              <div>
                <p className="text-[11px] uppercase tracking-widest text-emerald-300 font-semibold">Certificado válido</p>
                <h1 className="text-2xl font-bold text-[#0B0B0B] font-display">Verificación exitosa</h1>
              </div>
            </div>

            <div className="mt-8 grid gap-6">
              <Row label="Titular" value={<span className="text-[#0B0B0B] text-lg font-semibold">{d.holder_name}</span>} />
              <Row label="Programa" value={<span className="text-[#0B0B0B] text-lg">{d.program_title}</span>} />
              <div className="grid grid-cols-2 gap-6">
                <Row label="Formato" value={<span className="text-[#0B0B0B] uppercase font-semibold text-sm">{d.program_format}</span>} />
                <Row label="Fecha de emisión" value={<span className="text-[#0B0B0B] font-mono text-sm">{new Date(d.issued_at).toLocaleDateString("es-MX", { day: "2-digit", month: "long", year: "numeric" })}</span>} />
              </div>
              <Row label="Folio" value={<span className="text-[#0B0B0B] font-mono text-sm" data-testid="verify-folio">{d.folio}</span>} />
              <Row label={<span className="flex items-center gap-1"><Building2 className="w-3 h-3" /> Emitido por</span>} value={<span className="text-[#0B0B0B]">{d.tenant_name}</span>} />
              <Row label="Hash de verificación" value={<span className="text-gray-600 font-mono text-[11px] break-all">{d.verification_hash}</span>} />
            </div>

            <div className="mt-8 pt-6 border-t border-gray-200 flex items-center gap-2 text-xs text-gray-500">
              <Award className="w-4 h-4" />
              Este certificado fue verificado en tiempo real contra la base de datos de Debt Capital College.
            </div>
            <div className="mt-4">
              <LinkedInShare folio={d.folio} programTitle={d.program_title} issuedAt={d.issued_at} />
            </div>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}

function Row({ label, value }) {
  return (
    <div>
      <p className="text-[11px] uppercase tracking-widest text-gray-600 font-semibold">{label}</p>
      <div className="mt-1">{value}</div>
    </div>
  );
}
